import { useEffect, useState } from 'react';
import { adminApi, type AdminDlqRow } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ReasonModal } from '@/components/ReasonModal';
import { useErrorToast, useToast } from '@/lib/toast';
import { formatTime } from '@/util/format';

const STATUSES = ['active', 'requeued', 'discarded', 'all'] as const;
type Status = (typeof STATUSES)[number];

type Confirm = { kind: 'requeue'; row: AdminDlqRow } | { kind: 'discard'; row: AdminDlqRow } | null;
type StateKey = Exclude<Status, 'all'>;

const STATE_VARIANT: Record<StateKey, { label: string; className: string }> = {
  active: {
    label: 'active',
    className: 'border-amber-500/30 bg-amber-500/10 text-amber-500',
  },
  requeued: {
    label: 'requeued',
    className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500',
  },
  discarded: {
    label: 'discarded',
    className: 'border-red-500/30 bg-red-500/10 text-red-500',
  },
};

function StateBadge({ row }: { row: AdminDlqRow }) {
  const key: StateKey = row.requeuedAt ? 'requeued' : row.discardedAt ? 'discarded' : 'active';
  const v = STATE_VARIANT[key];
  return (
    <Badge variant="outline" className={`uppercase tracking-wide ${v.className}`}>
      {v.label}
    </Badge>
  );
}

export function DLQPage() {
  const [orgId, setOrgId] = useState('');
  const [status, setStatus] = useState<Status>('active');
  const [rows, setRows] = useState<AdminDlqRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const onError = useErrorToast();
  const toast = useToast();

  async function refresh() {
    setBusy(true);
    try {
      const r = await adminApi.listDlq({
        orgId: orgId.trim() || undefined,
        status,
        limit: 100,
      });
      setRows(r.data);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold">Dead-letter queue</h1>
        <p className="text-sm text-muted-foreground">
          Jobs that exhausted retries. Requeue to retry, or discard if the work is no longer needed.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_180px_auto]">
        <Input
          placeholder="Filter by org id"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
        />
        <Select value={status} onValueChange={(v) => setStatus(v as Status)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button disabled={busy} onClick={() => void refresh()}>
          Search
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>First seen</TableHead>
              <TableHead>Job</TableHead>
              <TableHead>Org</TableHead>
              <TableHead>Attempts</TableHead>
              <TableHead>Error</TableHead>
              <TableHead>State</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows?.map((r) => {
              const isResolved = r.requeuedAt || r.discardedAt;
              return (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatTime(r.firstSeen)}
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-xs">{r.jobType}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">
                      {r.jobId ?? r.id}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.organizationId ?? '—'}</TableCell>
                  <TableCell>{r.attempts}</TableCell>
                  <TableCell className="max-w-[320px]">
                    <span className="text-xs text-muted-foreground">{r.errorMessage ?? '—'}</span>
                  </TableCell>
                  <TableCell>
                    <StateBadge row={r} />
                  </TableCell>
                  <TableCell className="text-right">
                    {!isResolved && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" onClick={() => setConfirm({ kind: 'requeue', row: r })}>
                          Requeue
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setConfirm({ kind: 'discard', row: r })}
                        >
                          Discard
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  No DLQ messages.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <ReasonModal
        open={confirm?.kind === 'requeue'}
        title="Requeue this job?"
        description={
          confirm?.kind === 'requeue'
            ? `Will reset attempt count and re-enqueue ${confirm.row.jobType}.`
            : ''
        }
        confirmLabel="Requeue"
        confirmKind="default"
        reasonOptional
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={async () => {
          if (confirm?.kind !== 'requeue') {
            return;
          }
          try {
            await adminApi.requeueDlq(confirm.row.id);
            toast.success('Requeued.');
            await refresh();
          } catch (e) {
            onError(e);
          }
        }}
      />
      <ReasonModal
        open={confirm?.kind === 'discard'}
        title="Discard this job?"
        description="The job will not be retried. This is permanent."
        confirmLabel="Discard"
        confirmKind="destructive"
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={async (reason) => {
          if (confirm?.kind !== 'discard') {
            return;
          }
          try {
            await adminApi.discardDlq(confirm.row.id, reason);
            toast.success('Discarded.');
            await refresh();
          } catch (e) {
            onError(e);
          }
        }}
      />
    </>
  );
}
